
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', delay = 0, className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; delay?: number; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation, delay)} ${className}`}>
      {children}
    </div>
  );
}

export default function Workflow() {
  const steps = [
    {
      number: '01',
      icon: 'ri-user-add-line',
      title: 'Registro del Paciente',
      description: 'Crea fichas digitales completas con datos personales, antecedentes médicos y motivo de consulta en segundos.',
    },
    {
      number: '02',
      icon: 'ri-eye-line',
      title: 'Examen Oftalmológico',
      description: 'Registra agudeza visual, refracción, presión intraocular, fondo de ojo y todos los hallazgos clínicos de forma estructurada.',
    },
    {
      number: '03',
      icon: 'ri-file-text-line',
      title: 'Diagnóstico y Tratamiento',
      description: 'Documenta diagnósticos con códigos CIE-10, genera recetas digitales y planifica tratamientos con seguimiento automático.',
    },
    {
      number: '04',
      icon: 'ri-line-chart-line',
      title: 'Seguimiento Continuo',
      description: 'Programa controles, envía recordatorios automáticos y compara la evolución del paciente entre visitas con gráficos interactivos.',
    },
  ];

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Flujo de Trabajo</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Así funciona <span className="font-display italic text-emerald-600">Anamnesy Soft</span>
            </h2>
            <p className="text-sm text-gray-500 max-w-lg mx-auto">
              Un flujo clínico optimizado que acompaña cada paso de la atención oftalmológica.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <AnimatedSection key={index} animation="slide-up" delay={index * 120}>
              <div className="relative group h-full">
                <div className="bg-white p-7 rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all h-full cursor-pointer">
                  <div className="flex items-center justify-between mb-5">
                    <span className="text-3xl font-bold text-emerald-100 group-hover:text-emerald-200 transition-colors">{step.number}</span>
                    <div className="w-11 h-11 bg-emerald-50 rounded-lg flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                      <i className={`${step.icon} text-emerald-600 text-xl`}></i>
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 flex items-center justify-center z-10">
                    <i className="ri-arrow-right-s-line text-emerald-300 text-xl"></i>
                  </div>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
